/**
 * Why a drop onto a folder is refused, decided before any request (the
 * server checks the same things again). Entity words follow today's copy.
 */

import { FOLDER_WORD } from '../../../theme/entityIcons';
import { canDropOnTarget } from '../../../utils/hierarchyValidation';
import type { DraggedItem } from '../../navigation/HierarchyTree.dnd';

/** Where files or items land: a folder, or the workspace root (id null). */
export interface DropFolder {
  readonly id: string | null;
  readonly name: string;
  readonly canEdit: boolean;
}

export const readOnlyRefusal = (target: DropFolder) =>
  `You can't add to ${target.name}. Ask its owner for edit access.`;

/**
 * The refusal for moving `dragged` into `target`, or null to go ahead.
 * `targetAncestorIds` are every folder above the target that the client
 * knows of; a dragged folder among them would end up inside itself.
 */
export function moveRefusal(
  dragged: ReadonlyArray<DraggedItem>,
  target: DropFolder & { readonly id: string },
  targetAncestorIds: ReadonlySet<string>,
): string | null {
  if (!target.canEdit) return readOnlyRefusal(target);
  for (const item of dragged) {
    // The tree's own rule: never onto itself.
    const type = item.kind === 'folder' ? 'project' : 'attachment';
    if (!canDropOnTarget(type, item.id, 'project', target.id)) {
      return `Can't move ${item.name} into itself.`;
    }
    if (item.kind === 'folder' && targetAncestorIds.has(item.id)) {
      return `Can't move ${item.name} into a ${FOLDER_WORD} inside it.`;
    }
  }
  return null;
}
